import * as Sentry from '@sentry/react';
import * as t from 'io-ts';
import { createActions } from 'redux-actions';

import { APP_VERSION, ENDPOINT_VERSION, ENDPOINTS_AUTH, ENDPOINTS_CONFIGURATION, ENDPOINTS_USER, ENVIRONMENT } from '../config/';
import { localeData } from '../locales/data';
import { AuthenticationAction, UserInfo, UserSettingsInfo } from '../reducers/AuthenticationReducer/AuthenticationReducer.types';
import { getInquiryIdSelector, getIsLoggedInSelector } from '../reducers/AuthenticationReducer/AuthenticationSelector';
import { SUPPORT_URL_PLACEHOLDER } from '../shared/constants';
import { getAuthCreds, getAuthToken, setAuthToken } from '../shared/helpers/authStorage';
import {
  defaultErrorMessage,
  globalFetch,
  ioTsValidationError,
  isAction,
  webSimApiErrorFlashMessages,
} from '../shared/helpers/fetch-helper';
import { redirectToHelp } from '../shared/helpers/redirect';
import { logErrorService } from '../shared/services';
import analytics from '../shared/services/analytics/analytics';

import {
  accountChangeFieldConnectSaved,
  cleanAccount,
  loginToBrainLabs,
  loginToWorkday,
  updateConsultantRequiredFields,
} from './AccountActions/AccountActions';
import { checkIfServerIsDown, createAccountfetchErrors } from './AccountActions/Helper';
import { getConsultant } from './ConsultantActions';
import { optionsLogout } from './EditorActions/EditorActions';
import { addFlashMessages } from './FlashMessagesActions';
import { emptyRequest, invalidRequest } from './GenericActions';
import { getLastMessages } from './MessagesActions';
import { profilePerformanceClear } from './Profile/PerformanceActions';
import { AppDispatch, GetState } from '../shared/services/store';
import { encodeToBase64 } from '../shared/helpers/encodeToBase64';
import { resetAgreementsState } from './AgreementsActions';
import { queryClient } from '../shared/api/apiHooks';
import { CAPTCHA_RETRY_SYMBOL, captchaExecuteAsync } from '../reducers/CaptchaReducer/CaptchaActions';
import { CaptchaReturnType } from '../reducers/CaptchaReducer/Captcha.type';
import { geniusActions } from '../reducers/GeniusProgramReducer/GeniusReducer';

import { setInitialTutorialState } from '../reducers/TutorialReducer/TutorialReducer';
import { setInitialConsultantTutorialState } from '../reducers/ConsultantTutorialReducer/ConsultantTutorialReducer';
import { hydrateCustomPreferencesFromUser } from '../reducers/CustomPreferencesReducer/CustomPreferencesReducer';
import { applyResolvedUserLanguage } from '../shared/services/i18n';

export const setErrorMsg = (msg: any) => {
  let errors: string[] = [];
  Object.keys(msg).forEach((key: string) => {
    msg[key].forEach((value: string) => {
      const message = `The Field ${key.charAt(0).toUpperCase()}${key.slice(1)}: ${value.replace('This field ', '')}`;
      errors = [...errors, ...[message]];
    });
  });

  return errors;
};

export const {
  requestUserSettingsSuccess,
  authenticationLogin,
  saveUserData,
  configurationResponse,
  authenticationRequest,
  authenticationRequestFailure,
  authenticationLogoutRequest,
  authenticationLogoutRequestFailure,
  authenticationLogout,
  requestUserInfo,
  requestUserInfoFailure,
  requestUserSettings,
  requestUserSettingsFailure,
  requestSetUserSettings,
  requestSetUserSettingsSuccess,
  requestSetUserSettingsFailure,
  requestForgotPassword,
  responseForgotPasswordFailure,
  responseForgotPassword,
  requestResetPassword,
  responseResetPasswordFailure,
  responseResetPassword,
  requireSelfie,
  authenticationExpire,
  requestUserAgreements,
  responseUserAgreementsFailure,
  responseUserAgreementsSuccess,
  requestReverify,
  responseReverify,
  responseReverifyFailure,
  clearReverify,
  clearSelfie,
  hideMessage,
  clearForgotPassword,
  requestUserChangePassword,
  responseUserChangePasswordFailure,
  responseUserChangePasswordSuccess,
  clearChangePasswordFetch,
  setRedirect,
  setRedirectLoading,
  setSelfieError,
  resetSelfieError,
} = createActions(
  {
    REQUEST_USER_SETTINGS_SUCCESS: (json: any) => json,
    AUTHENTICATION_LOGIN: (json: any) => json,
    SAVE_USER_DATA: (json: any, competition, isConsultant) => ({ json, competition, isConsultant }),
    CONFIGURATION_RESPONSE: (json: any) => json,
    SET_REDIRECT: (redirectUrl?: string) => ({ redirectUrl }),
    REQUIRE_SELFIE: (challengeDetails: AuthenticationAction) => challengeDetails,
  },
  'AUTHENTICATION_REQUEST',
  'AUTHENTICATION_REQUEST_FAILURE',
  'AUTHENTICATION_LOGOUT_REQUEST',
  'AUTHENTICATION_LOGOUT_REQUEST_FAILURE',
  'AUTHENTICATION_LOGOUT',
  'REQUEST_USER_INFO',
  'REQUEST_USER_INFO_FAILURE',
  'REQUEST_USER_SETTINGS',
  'REQUEST_USER_SETTINGS_FAILURE',
  'REQUEST_SET_USER_SETTINGS',
  'REQUEST_SET_USER_SETTINGS_SUCCESS',
  'REQUEST_SET_USER_SETTINGS_FAILURE',
  'REQUEST_FORGOT_PASSWORD',
  'RESPONSE_FORGOT_PASSWORD_FAILURE',
  'RESPONSE_FORGOT_PASSWORD',
  'REQUEST_RESET_PASSWORD',
  'RESPONSE_RESET_PASSWORD_FAILURE',
  'RESPONSE_RESET_PASSWORD',
  'AUTHENTICATION_EXPIRE',
  'REQUEST_USER_AGREEMENTS',
  'RESPONSE_USER_AGREEMENTS_FAILURE',
  'RESPONSE_USER_AGREEMENTS_SUCCESS',
  'REQUEST_REVERIFY',
  'RESPONSE_REVERIFY',
  'RESPONSE_REVERIFY_FAILURE',
  'CLEAR_REVERIFY',
  'CLEAR_SELFIE',
  'HIDE_MESSAGE',
  'CLEAR_FORGOT_PASSWORD',
  'REQUEST_USER_CHANGE_PASSWORD',
  'RESPONSE_USER_CHANGE_PASSWORD_FAILURE',
  'RESPONSE_USER_CHANGE_PASSWORD_SUCCESS',
  'CLEAR_CHANGE_PASSWORD_FETCH',
  'SET_REDIRECT_LOADING',
  'SET_SELFIE_ERROR',
  'RESET_SELFIE_ERROR'
);
const optionsSuportLink = {
  links: [
    {
      urls: [SUPPORT_URL_PLACEHOLDER],
      keys: ['support-link'],
      defaultMsgkeys: [localeData.en['support-link']],
    },
  ],
};
// tslint:disable-next-line:max-func-body-length
export const errorMessageLogin = (details: string) => {
  switch (details) {
    case 'NOT_VERIFIED':
      return {
        message: [
          {
            msg: 'flash.message.auth.not-verified',
            defaultMessage: localeData.en['flash.message.auth.not-verified'],
          },
        ],
        options: {
          links: [
            {
              urls: ['/reverify', SUPPORT_URL_PLACEHOLDER],
              keys: ['flash.message.auth.not-verified-link', 'support-link'],
              defaultMsgkeys: [localeData.en['flash.message.auth.not-verified-link'], localeData.en['support-link']],
            },
          ],
        },
      };
    case 'NO_CREDENTIALS':
      return {
        message: [
          {
            msg: 'flash.message.auth.no-credentials',
            defaultMessage: localeData.en['flash.message.auth.no-credentials'],
          },
        ],
      };
    case 'INVALID_CREDENTIALS':
      return {
        message: [
          {
            msg: 'flash.message.auth.invalid-credentials',
            defaultMessage: localeData.en['flash.message.auth.invalid-credentials'],
          },
        ],
      };
    case 'NOT_APPROVED':
      return {
        message: [{ msg: 'flash.message.auth.not-approved', defaultMessage: localeData.en['flash.message.auth.not-approved'] }],
        options: optionsSuportLink,
      };
    case 'INACTIVE':
      return {
        message: [{ msg: 'flash.message.auth.inactive', defaultMessage: localeData.en['flash.message.auth.inactive'] }],
        options: optionsSuportLink,
      };
    case 'DISABLED':
      return {
        message: [{ msg: 'flash.message.auth.disabled', defaultMessage: localeData.en['flash.message.auth.disabled'] }],
        options: {
          ...optionsSuportLink,
          excludedLinks: [0],
        },
      };
    case 'LOCKED':
      return {
        message: [{ msg: 'flash.message.auth.locked', defaultMessage: localeData.en['flash.message.auth.locked'] }],
        options: optionsSuportLink,
      };
    case 'DEACTIVATED':
      return {
        message: [{ msg: 'flash.message.auth.deactivated', defaultMessage: localeData.en['flash.message.auth.deactivated'] }],
        options: optionsSuportLink,
      };
    case 'INQUIRY_INCOMPLETE':
      return {
        message: [
          {
            msg: 'flash.message.auth.verificationIncomplete',
            defaultMessage: localeData.en['flash.message.auth.verificationIncomplete'],
          },
        ],
      };
    case 'RECERTIFICATION_FAILED':
      return {
        message: [
          {
            msg: 'flash.message.auth.recertification-failed',
            defaultMessage: localeData.en['flash.message.auth.recertification-failed'],
          },
        ],
        options: optionsSuportLink,
      };
    case 'CONSULTANT_COUNTRY_CHANGE':
      return {
        message: [
          {
            msg: 'flash.message.auth.consultant-country-change',
            defaultMessage: localeData.en['flash.message.auth.consultant-country-change'],
          },
        ],
        options: optionsSuportLink,
      };
    case 'ALPHA_SELLER':
      return {
        message: [{ msg: 'flash.message.auth.alpha-seller', defaultMessage: localeData.en['flash.message.auth.alpha-seller'] }],
        options: optionsSuportLink,
      };
    case 'THROTTLED':
      return {
        message: [
          {
            msg: 'flash.message.auth.throttled',
            defaultMessage: localeData.en['flash.message.auth.throttled'],
          },
        ],
      };
    case 'AGREEMENT_EXPIRED':
      return {
        message: [{ msg: 'flash.message.auth.agreement-expired', defaultMessage: localeData.en['flash.message.auth.agreement-expired'] }],
        options: optionsSuportLink,
      };
    case 'BIOMETRICS_THROTTLED':
      return {
        message: [
          { msg: 'flash.message.auth.persona.tooManyLogins', defaultMessage: localeData.en['flash.message.auth.persona.tooManyLogins'] },
        ],
        options: optionsSuportLink,
      };
    default:
      return {
        message: [{ msg: 'flash.message.auth.disabled', defaultMessage: localeData.en['flash.message.auth.disabled'] }],
        options: {
          ...optionsSuportLink,
          excludedLinks: [0],
        },
      };
  }
};

export const getIsWorkdayUrl = (url: string) => {
  try {
    let parsedUrl: URL;
    parsedUrl = new URL(url);

    return parsedUrl.host.includes('workday.com');
  } catch {
    return false;
  }
};

export const openWorkdayWithLoginCredentials =
  () =>
  (dispatch: any): void => {
    const authCreds = getAuthCreds();
    if (authCreds == null) {
      logErrorService('No credentials provided for workday', authCreds);

      return;
    }

    dispatch(
      accountChangeFieldConnectSaved({
        id: 'password',
        name: 'workdayPassword',
        type: 'password',
        value: authCreds.password,
        autoFocus: false,
        tabIndex: 1,
        className: 'form__input',
        disabled: false,
      })
    );
    dispatch(saveUserData({ email: authCreds.email, address: {} }));
    dispatch(loginToWorkday());
  };

const handleUserRedirect = (dispatch: any, state: any) => {
  const redirectUrl = state.getIn(['user', 'redirectUrl']);
  if (getIsWorkdayUrl(redirectUrl)) {
    dispatch(openWorkdayWithLoginCredentials());
  } else {
    redirectToHelp(redirectUrl);
  }

  setRedirect();
};
export const openBrainLabsWithLoginCredentials =
  () =>
  (dispatch: any): void => {
    const authCreds = getAuthCreds();
    if (authCreds == null) {
      logErrorService('No credentials provided for BrainLabs', authCreds);

      return;
    }

    dispatch(
      accountChangeFieldConnectSaved({
        id: 'password',
        name: 'brainLabs',
        type: 'password',
        value: authCreds.password,
        autoFocus: false,
        tabIndex: 1,
        className: 'form__input',
        disabled: false,
      })
    );
    dispatch(saveUserData({ email: authCreds.email, address: {} }));
    dispatch(loginToBrainLabs());
  };

export const userLogoutIfLoggedOut =
  (shouldShowFlashMessage: boolean = true) =>
  async (dispatch: AppDispatch, getState: () => any): Promise<object> => {
    const state = getState();
    dispatch(authenticationLogoutRequest());
    Sentry.setUser(null);
    try {
      analytics.clearUserId();
    } catch (err) {
      logErrorService(err, err);
    }
    dispatch(authenticationLogout());
    dispatch(cleanAccount());
    dispatch(resetAgreementsState());
    dispatch(profilePerformanceClear());
    dispatch(geniusActions.reset());
    if (shouldShowFlashMessage && state.getIn(['user', 'data'])) {
      dispatch(addFlashMessages([{ msg: 'session.expire.message', defaultMessage: localeData.en['session.expire.message'] }]));
    }

    return dispatch(optionsLogout());
  };

export const sessionExpire =
  (shouldShowFlashMessage: boolean = true) =>
  async (dispatch: any): Promise<object> =>
    fetch(ENDPOINTS_AUTH.login, {
      method: 'DELETE',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => response.json())
      .then(async (): Promise<object> => {
        Sentry.setUser(null);
        try {
          analytics.clearUserId();
        } catch (err) {
          logErrorService(err, err);
        }

        return dispatch(userLogoutIfLoggedOut(shouldShowFlashMessage))
          .then(() => dispatch(authenticationExpire()))
          .catch(() => dispatch(authenticationExpire()));
      })
      .catch(() => dispatch(authenticationExpire()));

export const setUserSettings =
  (
    data: {
      region: string;
      universe: string;
      delay: number;
      decay: number;
      neutralization: string;
      truncation: number;
      language: string;
      visualization: boolean;
      testPeriod?: string;
      maxTrade?: string;
      maxPosition?: string;
    },
    id: string
  ) =>
  async (dispatch: any): Promise<object> => {
    if (id === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestSetUserSettings());
    if (t.exact(UserSettingsInfo).decode(data).isRight()) {
      return globalFetch(
        `${ENDPOINTS_USER.user}/${id}/settings/simulation`,
        {
          method: 'POST',
          credentials: 'include',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(data),
        },
        dispatch,
        () => dispatch(requestSetUserSettingsFailure())
      )
        .then(async (json: any): Promise<object> => {
          if (isAction(json, 'REQUEST_SET_USER_SETTINGS_FAILURE')) {
            return json;
          } else if (t.exact(UserSettingsInfo).decode(json).isRight()) {
            dispatch(requestSetUserSettingsSuccess());
            dispatch(
              addFlashMessages(
                [
                  {
                    msg: 'simulation.settings.saved.default.settings.message',
                    defaultMessage: localeData.en['simulation.settings.saved.default.settings.message'],
                  },
                ],
                { type: 'info' }
              )
            );

            return dispatch(requestUserSettingsSuccess(json));
          } else {
            const errors = setErrorMsg(json);
            if (errors.length !== 0) {
              dispatch(addFlashMessages(errors, { location: 'top', context: 'generic', intl: false }));
            }
            logErrorService(errors, errors);

            return dispatch(requestSetUserSettingsFailure());
          }
        })
        .catch(() => {
          defaultErrorMessage(dispatch);
          logErrorService(
            'WorldQuant BRAIN is experiencing some difficulties. Please contact support if this problem persists.',
            'WorldQuant BRAIN is experiencing some difficulties. Please contact support if this problem persists.'
          );

          return dispatch(requestSetUserSettingsFailure());
        });
    } else {
      defaultErrorMessage(dispatch);
      logErrorService(
        'WorldQuant BRAIN is experiencing some difficulties. Please contact support if this problem persists.',
        'WorldQuant BRAIN is experiencing some difficulties. Please contact support if this problem persists.'
      );

      return dispatch(requestSetUserSettingsFailure());
    }
  };

export const getUserSettings =
  (id: string) =>
  async (dispatch: any): Promise<object> => {
    if (id === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestUserSettings());

    return globalFetch(
      `${ENDPOINTS_USER.user}/${id}/settings/simulation`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(requestUserSettingsFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'REQUEST_USER_SETTINGS_FAILURE')) {
          return json;
        } else if (t.exact(UserSettingsInfo).decode(json).isRight()) {
          return dispatch(requestUserSettingsSuccess(json));
        } else {
          defaultErrorMessage(dispatch);
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, UserSettingsInfo);

          return dispatch(requestUserSettingsFailure());
        }
      })
      .catch(() => {
        defaultErrorMessage(dispatch);
        logErrorService(
          'WorldQuant BRAIN is experiencing some difficulties. Please contact support if this problem persists.',
          'WorldQuant BRAIN is experiencing some difficulties. Please contact support if this problem persists.'
        );

        return dispatch(requestUserSettingsFailure());
      });
  };

export const getConfiguration =
  () =>
  async (dispatch: any): Promise<object> => {
    dispatch(authenticationRequest());

    return globalFetch(
      `${ENDPOINTS_CONFIGURATION.configuration}`,
      {
        method: 'GET',
      },
      dispatch,
      () => dispatch(authenticationRequestFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'AUTHENTICATION_REQUEST_FAILURE')) {
          return json;
        } else {
          return dispatch(configurationResponse(json));
        }
      })
      .catch(() => {
        dispatch(
          addFlashMessages([
            {
              msg: 'simulation.get.settings',
              defaultMessage: localeData.en['simulation.get.settings'],
            },
          ])
        );
        logErrorService(
          "It wasn't possible to get the required configuration data, try again later ",
          "It wasn't possible to get the required configuration data, try again later "
        );

        return dispatch(authenticationRequestFailure());
      });
  };

export const getUserInformation =
  (id: string, shouldRequestUserInfo: boolean = true) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<object> => {
    const state = getState();
    if (id === undefined) {
      return dispatch(invalidRequest());
    }

    if (shouldRequestUserInfo === true) {
      dispatch(requestUserInfo());
    }

    return globalFetch(
      `${ENDPOINTS_USER.user}/${id}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(authenticationRequestFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'AUTHENTICATION_REQUEST_FAILURE')) {
          return json;
        } else if (t.exact(UserInfo).decode(json).isRight()) {
          const fullName = `${json.firstName}${json.lastName}`;
          Sentry.setUser({
            id: json.id,
            email: json.email,
            name: `${json.fullName ? json.fullName : fullName}`,
          });

          try {
            analytics.setUserLevel(json.level);
          } catch (err) {
            logErrorService(err, err);
          }

          dispatch(hydrateCustomPreferencesFromUser(json.settings));
          applyResolvedUserLanguage({
            accountLanguage: json.settings?.client?.preferences?.language,
            country: json.address?.country,
          });
          return dispatch(saveUserData(json, state.getIn(['competitions', 'competition'])));
        } else {
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, UserInfo);

          return dispatch(requestUserInfoFailure());
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(authenticationRequestFailure());
      });
  };

/**
 * GET /api/authentication
 */
export const checkIfSessionExpired =
  (shouldCallLogout: boolean) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<object> =>
    fetch(`${ENDPOINTS_AUTH.login}`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        const state = getState();

        const isLoggedIn = getIsLoggedInSelector(state);

        if (response.status === 204) {
          // **Context**: Some routes that available for both logged-in / not logged-in users like IQC Landing,
          // Alphathon Landing, Referral Page,... will have `shouldCallLogout` is true. As we perform checking authentication
          // whenever visibility change and not logged-in users always receive 204 status code. It means that: we always
          // dispatch logout whenever visibility change for public routes if user is not logged in. It will cause
          // re-render App.tsx, and also cause re-render the whole route tree. Because of that, containers in route will
          // lose their state (component state, inputs will lose value,...)
          //
          // Finally: Make sure that we will not dispatch logout action when user is already logged out.
          if (isLoggedIn && shouldCallLogout) {
            // @ts-ignore todo: turn into real action or stop dispatching it
            dispatch(userLogoutIfLoggedOut());
          }

          return dispatch(authenticationExpire());
        } else if (response.ok) {
          return response.json();
        } else if (response.status === 401) {
          // @ts-ignore todo: turn into real action or stop dispatching it
          dispatch(userLogoutIfLoggedOut());
          dispatch(addFlashMessages([{ msg: 'session.expire.message', defaultMessage: localeData.en['session.expire.message'] }]));

          return dispatch(authenticationExpire());
        } else if (response.status === 404) {
          window.location.reload();

          return dispatch(emptyRequest());
        } else if (response.status >= 500 && response.status <= 504) {
          return checkIfServerIsDown(dispatch, response, () => dispatch(authenticationRequestFailure()));
        }

        return dispatch(authenticationExpire());
      })
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'AUTHENTICATION_EXPIRE')) {
          return json;
        }

        return dispatch(authenticationLogin(json));
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(authenticationRequestFailure());
      });

export const forgotPassword =
  (data: { email: string }) =>
  async (dispatch: any): Promise<object | typeof CAPTCHA_RETRY_SYMBOL> => {
    const captcha = await dispatch(captchaExecuteAsync());

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL;
    }

    dispatch(requestForgotPassword());

    return globalFetch(
      ENDPOINTS_USER.forgot_password,
      {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ ...data, captcha }),
      },
      dispatch,
      () => dispatch(responseForgotPasswordFailure()),
      {
        answerEmpty: true,
        callbackStatus400: async (response: any): Promise<object> =>
          response
            .json()
            .then(async (json: any): Promise<object> => createAccountfetchErrors(json, dispatch, responseForgotPasswordFailure)),
      }
    )
      .then((json: any) => {
        if (isAction(json, 'RESPONSE_FORGOT_PASSWORD_FAILURE')) {
          return json;
        } else if (json.type === 'EMPTY_REQUEST') {
          return dispatch(responseForgotPassword());
        } else {
          return createAccountfetchErrors(json, dispatch, responseForgotPasswordFailure);
        }
      })
      .catch((err: any) => {
        defaultErrorMessage(dispatch);
        logErrorService(err, err);

        return dispatch(responseForgotPasswordFailure());
      });
  };

export const userChangePassword =
  (data: { currentpassword: string; password: string; confirmation: string }) =>
  async (dispatch: any, getState: any): Promise<object | typeof CAPTCHA_RETRY_SYMBOL> => {
    const captcha = await dispatch(captchaExecuteAsync());

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL;
    }

    dispatch(requestUserChangePassword());

    const state = getState();
    const email = state.getIn(['user', 'data', 'email']);
    const dataInformation = `${email}:${data.currentpassword}`;
    const headerAuth = `Basic ${encodeToBase64(dataInformation)}`;

    return fetch(ENDPOINTS_USER.change_password, {
      method: 'POST',
      credentials: 'include',
      headers: new Headers({
        Authorization: headerAuth,
        'Content-Type': 'application/json',
        Accept: `application/json;${ENDPOINT_VERSION}`,
      }),
      body: JSON.stringify({ password: data.password, confirmation: data.confirmation, captcha }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.status === 200) {
          return dispatch(responseUserChangePasswordSuccess());
        } else if (response.status === 401) {
          logErrorService(localeData.en['edit.password.current.error'], localeData.en['edit.password.current.error']);
          dispatch(
            addFlashMessages([{ msg: 'edit.password.current.error', defaultMessage: localeData.en['edit.password.current.error'] }])
          );

          return dispatch(responseUserChangePasswordFailure());
        } else if (response.status >= 500 && response.status <= 504) {
          return checkIfServerIsDown(dispatch, response, () => dispatch(responseUserChangePasswordFailure()));
        } else {
          return response.json().then(async (json: any): Promise<object> => {
            const errors = setErrorMsg(json);
            if (errors.length !== 0) {
              dispatch(addFlashMessages(errors, { context: 'generic', intl: false }));
            }
            logErrorService(errors, errors);

            return dispatch(responseUserChangePasswordFailure());
          });
        }
      })
      .catch((err: any) => {
        dispatch(addFlashMessages([{ msg: 'edit.password.failure', defaultMessage: localeData.en['edit.password.failure'] }]));
        logErrorService(err, err);

        return dispatch(responseUserChangePasswordFailure());
      });
  };

export const reverify =
  (data: { email: string }) =>
  async (dispatch: any): Promise<object | typeof CAPTCHA_RETRY_SYMBOL> => {
    const captcha = await dispatch(captchaExecuteAsync());

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL;
    }

    const updatedData = { ...data, captcha };

    dispatch(requestReverify());

    return globalFetch(
      ENDPOINTS_USER.reverify_email,
      {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(updatedData),
      },
      dispatch,
      () => dispatch(responseReverifyFailure()),
      {
        answerEmpty: true,
        callbackStatus400: async (response: any): Promise<object> =>
          response.json().then(async (json: any): Promise<object> => createAccountfetchErrors(json, dispatch, responseReverifyFailure)),
      }
    )
      .then((json: any) => {
        if (isAction(json, 'RESPONSE_REVERIFY_FAILURE')) {
          return json;
        } else if (json.type === 'EMPTY_REQUEST') {
          return dispatch(responseReverify());
        } else {
          if (json.captcha && Array.isArray(json.captcha)) {
            const msg = String(json.captcha[0]);
            dispatch(addFlashMessages([{ msg, defaultMessage: msg }]));
          }

          return dispatch(responseReverifyFailure());
        }
      })
      .catch((err: any) => {
        dispatch(
          addFlashMessages([{ msg: 'resending.email.error.message', defaultMessage: localeData.en['resending.email.error.message'] }])
        );
        logErrorService(err, err);

        return dispatch(responseReverifyFailure());
      });
  };

export const resetPassword =
  (
    resetToken: string,
    data: {
      password: string;
      confirmation: string;
    }
  ) =>
  async (dispatch: any): Promise<object | typeof CAPTCHA_RETRY_SYMBOL> => {
    const captcha = await dispatch(captchaExecuteAsync());

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL;
    }

    dispatch(requestResetPassword());

    return fetch(ENDPOINTS_USER.reset_password, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${resetToken}`,
        Accept: `application/json;${ENDPOINT_VERSION}`,
      }),
      body: JSON.stringify({ ...data, captcha }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          return dispatch(responseResetPassword());
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);
          dispatch(
            addFlashMessages(
              [{ msg: 'reset.password.error.message', defaultMessage: localeData.en['reset.password.error.message'] }],
              optionsSuportLink
            )
          );

          return dispatch(responseForgotPasswordFailure());
        } else {
          logErrorService(response.statusText, response.status);

          return response
            .json()
            .then(async (json: any): Promise<object> => createAccountfetchErrors(json, dispatch, responseResetPasswordFailure));
        }
      })
      .catch((err: any) => {
        defaultErrorMessage(dispatch);
        logErrorService(err, err);

        return dispatch(responseResetPasswordFailure());
      });
  };

export const getUserAgreements =
  () =>
  async (dispatch: any): Promise<object> => {
    dispatch(requestUserAgreements());

    return globalFetch(
      `${ENDPOINTS_USER.user}/self/agreements`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseUserAgreementsFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_USER_AGREEMENTS_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(responseUserAgreementsFailure());
          logErrorService(json.detail, json.detail);

          return dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }]));
        } else {
          return dispatch(responseUserAgreementsSuccess(json));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);

        return dispatch(responseUserAgreementsFailure());
      });
  };

const handleLoginErrorStatus = async (response: any, dispatch: any, state: any): Promise<object> =>
  response.json().then(async (json: any): Promise<object> => {
    if (json) {
      if (json.inquiry) {
        return dispatch(requireSelfie({ inquiryId: json.inquiry }));
      }

      logErrorService(json, json);

      if (json.detail) {
        const errorMsg: any = errorMessageLogin(json.detail);

        if (errorMsg.hasOwnProperty('options')) {
          dispatch(addFlashMessages(errorMsg.message, errorMsg.options));
        } else {
          dispatch(addFlashMessages(errorMsg.message));
        }

        if (getInquiryIdSelector(state)) {
          dispatch(clearSelfie());
          dispatch(sessionExpire());
        }
      } else if (json.captcha) {
        const msg = String(json.captcha[0] ?? json.captcha);
        dispatch(addFlashMessages([{ msg, defaultMessage: msg }]));
      }
    }

    return dispatch(authenticationRequestFailure());
  });

const handleLoginOk = async (json: any, dispatch: any): Promise<object> => {
  Sentry.setUser({ id: json.user.id });
  try {
    analytics.setUserInfo(json.user.id, json.permissions);
    analytics.trackPredefinedEvent('SignIn');
  } catch (err) {
    logErrorService(err, err);
  }
  const userInformationPromise = dispatch(getUserInformation(json.user.id));
  dispatch(getLastMessages());
  const userAgreementsPromise = dispatch(getUserAgreements());

  if (json.permissions?.includes('TUTORIAL')) {
    dispatch(setInitialTutorialState());
  }

  return Promise.all([userInformationPromise, userAgreementsPromise]).then(() => dispatch(authenticationLogin(json)));
};

const handleAppVersion = async (json: any, dispatch: any): Promise<object> =>
  fetch('/version.json', {
    method: 'GET',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: `application/json;${ENDPOINT_VERSION}` }),
  })
    .then(
      async (response: any): Promise<object> =>
        response
          .json()
          .then(async (jsonResponse: any): Promise<object> => {
            if (jsonResponse && jsonResponse.version.replace(/["']/g, '') !== APP_VERSION) {
              window.location.reload();

              return dispatch(emptyRequest());
            } else {
              return handleLoginOk(json, dispatch);
            }
          })
          .catch(async (): Promise<object> => handleLoginOk(json, dispatch))
    )
    .catch(async (): Promise<object> => handleLoginOk(json, dispatch));

const handleLoginOkStatus = async (json: any, dispatch: any, state: any): Promise<object> => {
  if (isAction(json, 'AUTHENTICATION_REQUEST_FAILURE')) {
    return json;
  } else if (json.detail) {
    const errorMsg: any = errorMessageLogin(json.detail);

    if (errorMsg.hasOwnProperty('options')) {
      dispatch(addFlashMessages(errorMsg.message, errorMsg.options));
    } else {
      dispatch(addFlashMessages(errorMsg.message));
    }
    logErrorService(errorMsg, errorMsg);

    return dispatch(authenticationRequestFailure());
  } else {
    queryClient.clear();
    const redirectUrl = state.getIn(['user', 'redirectUrl']);

    if (redirectUrl) {
      handleUserRedirect(dispatch, state);

      return dispatch(setRedirectLoading());
    }
    if (ENVIRONMENT === 'production') {
      return handleAppVersion(json, dispatch);
    } else {
      return handleLoginOk(json, dispatch);
    }
  }
};

export const handleLoginCatch = async (err: any, dispatch: any): Promise<object> => {
  const msg = 'api.error.brain.experiencing';
  webSimApiErrorFlashMessages(dispatch);
  logErrorService(localeData.en[msg], err);

  return dispatch(authenticationRequestFailure());
};

export const fetchLogin =
  (data: { email: string; password: string }) =>
  async (dispatch: any, getState: any): Promise<object | typeof CAPTCHA_RETRY_SYMBOL> => {
    const state = getState();

    if (state.getIn(['user', 'isFetching']) === true) {
      return dispatch(authenticationRequest());
    }

    // Show the spinner immediately so the user gets feedback while the captcha
    // PoW is still being computed.
    dispatch(authenticationRequest());

    let captcha: CaptchaReturnType;
    try {
      captcha = await dispatch(captchaExecuteAsync());
    } catch (err) {
      dispatch(authenticationRequestFailure());
      throw err;
    }

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      dispatch(authenticationRequestFailure());
      return CAPTCHA_RETRY_SYMBOL;
    }

    const dataWithCaptcha = { ...data, captcha };

    setAuthToken(dataWithCaptcha);
    const headerAuth = getAuthToken();

    const headerLogIn: any = {
      method: 'POST',
      credentials: 'include',
      headers: new Headers({
        Authorization: headerAuth as string,
        'Content-Type': 'application/json',
        Accept: `application/json;${ENDPOINT_VERSION}`,
      }),
      body: JSON.stringify({
        captcha: dataWithCaptcha.captcha,
      }),
    };

    return fetch(ENDPOINTS_AUTH.login, headerLogIn)
      .then(async (response: any): Promise<object> => {
        if (response.status >= 500 && response.status <= 504) {
          return checkIfServerIsDown(dispatch, response, () => dispatch(authenticationRequestFailure()));
        } else if (response.status === 401 || response.status === 403 || response.status === 429 || response.status === 400) {
          logErrorService(response.statusText, response.status);

          return handleLoginErrorStatus(response, dispatch, state);
        } else {
          return response
            .json()
            .then(async (json: any): Promise<object> => handleLoginOkStatus(json, dispatch, state))
            .catch((err: any) => {
              const msg = 'api.error.brain.experiencing';
              webSimApiErrorFlashMessages(dispatch);
              logErrorService(localeData.en[msg], err);

              return dispatch(authenticationRequestFailure());
            });
        }
      })
      .catch((err: any) => {
        const msg = 'api.error.brain.experiencing';
        webSimApiErrorFlashMessages(dispatch);
        logErrorService(localeData.en[msg], err);

        return dispatch(authenticationRequestFailure());
      });
  };

export const setBiometricCancelled =
  () =>
  async (dispatch: any): Promise<object> => {
    const msg = 'authenticationMain.biometric.cancelled';
    dispatch(addFlashMessages([{ msg: msg, defaultMessage: msg }]));

    dispatch(clearSelfie());

    return dispatch(sessionExpire());
  };

export const fetchBiometricSignIn =
  (inquiryId: string) =>
  async (dispatch: any, getState: any): Promise<object | typeof CAPTCHA_RETRY_SYMBOL> => {
    const state = getState();

    let captcha: CaptchaReturnType;
    try {
      captcha = await dispatch(captchaExecuteAsync());
    } catch (err) {
      dispatch(clearSelfie());
      dispatch(authenticationRequestFailure());
      throw err;
    }

    if (captcha === CAPTCHA_RETRY_SYMBOL) {
      dispatch(clearSelfie());
      dispatch(authenticationRequestFailure());
      return CAPTCHA_RETRY_SYMBOL;
    }

    const signInRequestOptions = {
      method: 'POST',
      credentials: 'include' as RequestCredentials,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ inquiry: inquiryId, captcha }),
    };

    return fetch(ENDPOINTS_AUTH.persona, signInRequestOptions)
      .then(async (response: any): Promise<object> => {
        if (response.status >= 500 && response.status <= 504) {
          return checkIfServerIsDown(dispatch, response, () => dispatch(authenticationRequestFailure()));
        } else if (response.status === 401 || response.status === 403 || response.status === 429 || response.status === 400) {
          logErrorService(response.statusText, response.status);

          return handleLoginErrorStatus(response, dispatch, state);
        } else {
          return response
            .json()
            .then(async (json: any): Promise<object> => {
              dispatch(clearSelfie());

              return handleLoginOkStatus(json, dispatch, state);
            })
            .catch((err: any) => {
              const msg = 'api.error.webSim.experiencing';
              webSimApiErrorFlashMessages(dispatch);
              logErrorService(localeData.en[msg], err);

              return dispatch(authenticationRequestFailure());
            });
        }
      })
      .catch((err: any) => {
        const msg = 'api.error.webSim.experiencing';
        webSimApiErrorFlashMessages(dispatch);
        logErrorService(localeData.en[msg], err);

        return dispatch(authenticationRequestFailure());
      });
  };

/**
 * DELETE /api/authentication
 */
export const userLogout =
  () =>
  async (dispatch: any): Promise<object> => {
    dispatch(authenticationLogoutRequest());

    return fetch(ENDPOINTS_AUTH.login, {
      method: 'DELETE',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (): Promise<object> => {
        Sentry.setUser(null);
        try {
          analytics.clearUserId();
        } catch (err) {
          logErrorService(err, err);
        }
        dispatch(authenticationLogout());
        dispatch(cleanAccount());
        dispatch(resetAgreementsState());
        dispatch(profilePerformanceClear());
        dispatch(geniusActions.reset());

        return dispatch(optionsLogout());
      })
      .catch((err: any) => {
        dispatch(
          addFlashMessages([
            {
              msg: 'flash.message.auth.invalid-credentials',
              defaultMessage: localeData.en['flash.message.auth.invalid-credentials'],
            },
          ])
        );
        logErrorService(err, err);

        return dispatch(authenticationLogoutRequestFailure());
      });
  };

export const checkIfAuth =
  () =>
  async (dispatch: any): Promise<object> => {
    dispatch(authenticationRequest());

    return globalFetch(
      ENDPOINTS_AUTH.login,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(authenticationRequestFailure()),
      {
        answerEmpty: false,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'AUTHENTICATION_REQUEST_FAILURE')) {
          return json;
        } else if (json && json.user) {
          const userId = json.user.id;
          Sentry.setUser({
            id: userId,
          });
          try {
            analytics.setUserInfo(json.user.id, json.permissions);
          } catch (err) {
            logErrorService(err, err);
          }

          const userPromises = [dispatch(getUserInformation(userId)), dispatch(getUserAgreements())];
          if (json.permissions?.includes('CONSULTANT')) {
            userPromises.push(dispatch(getConsultant(userId)));
            userPromises.push(dispatch(setInitialConsultantTutorialState()));
            setTimeout(() => dispatch(updateConsultantRequiredFields()), 1000);
          }

          if (json.permissions?.includes('TUTORIAL')) {
            userPromises.push(dispatch(setInitialTutorialState()));
          }

          dispatch(getLastMessages());

          return Promise.all(userPromises).then(() => dispatch(authenticationLogin(json)));
        } else {
          return dispatch(authenticationRequestFailure());
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(authenticationRequestFailure());
      });
  };
